import { useState } from 'react'
import { CheckCircle2, XCircle, Clock, RefreshCw, CheckCheck, Ban, Inbox } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'

interface Update {
  id: number
  field: string
  oldValue: string
  newValue: string
  source: 'google' | 'user' | string
  status: 'pending' | 'accepted' | 'rejected' | string
  detectedAt: string
  resolvedAt?: string | null
}

interface UpdatesProps {
  locationId: number | null
}

// Human-readable field names
const FIELD_LABELS: Record<string, string> = {
  hours:         'Horaires d\'ouverture',
  name:          'Nom de l\'\u00e9tablissement',
  phone:         'T\u00e9l\u00e9phone',
  website:       'Site web',
  address:       'Adresse',
  description:   'Description',
  category:      'Cat\u00e9gorie',
  attributes:    'Attributs',
  photos:        'Photos',
}

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field
}

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (mins < 60)   return `il y a ${mins} min`
  if (hours < 24)  return `il y a ${hours}h`
  if (days === 1)  return 'hier'
  if (days < 30)   return `il y a ${days} jours`
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function SourceBadge({ source }: { source: string }) {
  const isGoogle = source === 'google'
  return (
    <span className={`
      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
      ${isGoogle
        ? 'bg-blue-50 text-blue-600 border border-blue-100'
        : 'bg-purple-50 text-purple-600 border border-purple-100'}
    `}>
      {isGoogle ? 'Google' : 'Utilisateur'}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'accepted') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">
        <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
        Accept&eacute;
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100">
      <XCircle className="w-3 h-3" strokeWidth={2} />
      Rejet&eacute;
    </span>
  )
}

export default function Updates({ locationId }: UpdatesProps) {
  const [tab, setTab] = useState<'pending' | 'history'>('pending')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [acting, setActing] = useState<Record<number, boolean>>({})
  const [bulkActing, setBulkActing] = useState(false)

  const { data: updates, loading, error, refetch } = useApi<Update[]>(
    locationId ? `/api/local/locations/${locationId}/updates` : ''
  )

  const allUpdates = asArray(updates)
  const pending = allUpdates.filter(u => u.status === 'pending')
  const history = allUpdates.filter(u => u.status !== 'pending')

  // ── Actions ──────────────────────────────────────────────────────────────

  async function handleSingle(id: number, action: 'accept' | 'reject') {
    setActing(prev => ({ ...prev, [id]: true }))
    await apiPost(`/api/local/updates/${id}/${action}`, {})
    setActing(prev => ({ ...prev, [id]: false }))
    refetch()
  }

  async function handleBulk(action: 'accept' | 'reject') {
    if (selected.size === 0) return
    setBulkActing(true)
    await apiPost('/api/local/updates/bulk-action', {
      updateIds: [...selected],
      action,
    })
    setBulkActing(false)
    setSelected(new Set())
    refetch()
  }

  // ── Selection helpers ─────────────────────────────────────────────────────

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selected.size === pending.length) setSelected(new Set())
    else setSelected(new Set(pending.map(u => u.id)))
  }

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!locationId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-[#8B8FA7] text-sm">S&eacute;lectionnez un &eacute;tablissement pour voir les suggestions.</p>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[#F4F5FA] rounded-xl p-1 w-fit">
        <button
          onClick={() => setTab('pending')}
          className={`
            flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all
            ${tab === 'pending'
              ? 'bg-white text-[#1B2559] shadow-sm'
              : 'text-[#555A73] hover:text-[#1B2559]'}
          `}
        >
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
          En attente
          {pending.length > 0 && (
            <span className="bg-[#6366F1] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
              {pending.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('history')}
          className={`
            flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all
            ${tab === 'history'
              ? 'bg-white text-[#1B2559] shadow-sm'
              : 'text-[#555A73] hover:text-[#1B2559]'}
          `}
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Historique
          {history.length > 0 && (
            <span className="bg-[#E2E4ED] text-[#555A73] text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
              {history.length}
            </span>
          )}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* ── PENDING TAB ── */}
      {!loading && !error && tab === 'pending' && (
        <>
          {pending.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
                <Inbox className="w-6 h-6 text-[#555A73]" strokeWidth={1.25} />
              </div>
              <p className="text-[#1B2559] font-semibold mb-1">Aucune suggestion en attente</p>
              <p className="text-[#8B8FA7] text-sm">Votre fiche est &agrave; jour &mdash; tout est en ordre.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Select all row */}
              <div className="flex items-center gap-2 px-1">
                <input
                  type="checkbox"
                  checked={selected.size === pending.length && pending.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-[#E2E4ED] text-[#6366F1] accent-[#6366F1] cursor-pointer"
                />
                <span className="text-xs text-[#8B8FA7]">
                  {selected.size > 0
                    ? `${selected.size} s&eacute;lectionn&eacute;${selected.size > 1 ? 's' : ''}`
                    : 'Tout s&eacute;lectionner'}
                </span>
              </div>

              {/* Cards */}
              {pending.map(upd => (
                <div
                  key={upd.id}
                  className={`
                    bg-white border rounded-xl p-4 space-y-3 transition-all
                    ${selected.has(upd.id) ? 'border-[#6366F1]/50 ring-1 ring-[#6366F1]/20' : 'border-[#E2E4ED]'}
                  `}
                >
                  {/* Top row */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(upd.id)}
                      onChange={() => toggleSelect(upd.id)}
                      className="w-4 h-4 rounded border-[#E2E4ED] mt-0.5 accent-[#6366F1] cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-sm font-semibold text-[#1B2559]">{fieldLabel(upd.field)}</span>
                        <SourceBadge source={upd.source} />
                        <span className="text-xs text-[#8B8FA7] ml-auto">{relativeDate(upd.detectedAt)}</span>
                      </div>

                      {/* Value diff */}
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-sm text-[#8B8FA7] line-through leading-relaxed break-all">
                          {upd.oldValue}
                        </span>
                        <span className="text-[#8B8FA7] text-sm">&rarr;</span>
                        <span className="text-sm text-emerald-600 font-medium leading-relaxed break-all">
                          {upd.newValue}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-end gap-2 pt-1 border-t border-[#F4F5FA]">
                    <button
                      onClick={() => handleSingle(upd.id, 'reject')}
                      disabled={!!acting[upd.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 transition-colors disabled:opacity-40"
                    >
                      <Ban className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Rejeter
                    </button>
                    <button
                      onClick={() => handleSingle(upd.id, 'accept')}
                      disabled={!!acting[upd.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-40"
                    >
                      <CheckCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Accepter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {!loading && !error && tab === 'history' && (
        <>
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-[#555A73]" strokeWidth={1.25} />
              </div>
              <p className="text-[#1B2559] font-semibold mb-1">Aucun historique</p>
              <p className="text-[#8B8FA7] text-sm">Les suggestions trait&eacute;es appara&icirc;tront ici.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(upd => (
                <div
                  key={upd.id}
                  className="bg-white border border-[#E2E4ED] rounded-xl p-4 space-y-3 opacity-80"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#1B2559]">{fieldLabel(upd.field)}</span>
                    <SourceBadge source={upd.source} />
                    <StatusBadge status={upd.status} />
                    <span className="text-xs text-[#8B8FA7] ml-auto">
                      {upd.resolvedAt ? relativeDate(upd.resolvedAt) : relativeDate(upd.detectedAt)}
                    </span>
                  </div>

                  {/* Value diff (read-only) */}
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-sm text-[#8B8FA7] line-through leading-relaxed break-all">
                      {upd.oldValue}
                    </span>
                    <span className="text-[#8B8FA7] text-sm">&rarr;</span>
                    <span className="text-sm text-[#555A73] leading-relaxed break-all">
                      {upd.newValue}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── BULK ACTION BAR ── */}
      {tab === 'pending' && selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#1B2559] text-white px-5 py-3 rounded-2xl shadow-xl">
          <span className="text-sm font-medium">
            {selected.size} s&eacute;lectionn&eacute;{selected.size > 1 ? 's' : ''}
          </span>
          <div className="w-px h-4 bg-white/20" />
          <button
            onClick={() => handleBulk('reject')}
            disabled={bulkActing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-40"
          >
            <Ban className="w-3.5 h-3.5" strokeWidth={1.5} />
            Rejeter
          </button>
          <button
            onClick={() => handleBulk('accept')}
            disabled={bulkActing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-40"
          >
            <CheckCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
            Accepter
          </button>
        </div>
      )}
    </div>
  )
}
